import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { Receipt, Plus, Check, X, CreditCard, AlertCircle, CheckCircle } from 'lucide-react';
import { EnterpriseToolbar, EnterpriseTableHead, StatusBadge, EmptyState, SelectCheckbox, RowActions } from '@/components/EnterpriseTable';

export default function PurchaseInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [grns, setGRNs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    vendor_id: '', grn_id: '', invoice_number: '', invoice_date: '', due_date: '',
    subtotal: 0, tax_amount: 0, total_amount: 0, payment_status: 'pending', notes: '',
    items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 18, amount: 0 }]
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invoicesRes, vendorsRes, grnsRes] = await Promise.all([
        api.get('/purchase-invoices').catch(() => ({ data: [] })),
        api.get('/vendors'),
        api.get('/grn')
      ]);
      setInvoices(invoicesRes.data);
      setVendors(vendorsRes.data);
      setGRNs(grnsRes.data.filter(g => g.status === 'confirmed'));
    } catch (err) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveInvoice = async () => {
    try {
      const subtotal = invoiceForm.items.reduce((s, i) => s + (i.quantity * i.unit_price), 0);
      const taxAmount = invoiceForm.items.reduce((s, i) => s + (i.quantity * i.unit_price * i.tax_rate / 100), 0);
      const payload = { ...invoiceForm, subtotal, tax_amount: taxAmount, total_amount: subtotal + taxAmount };

      if (selectedInvoice) {
        await api.put(`/purchase-invoices/${selectedInvoice.id}`, payload);
        toast.success('Invoice updated');
      } else {
        await api.post('/purchase-invoices', payload);
        toast.success('Invoice created');
      }
      setInvoiceDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save invoice');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await api.put(`/purchase-invoices/${invoiceId}/mark-paid`);
      toast.success('Invoice marked as paid');
      fetchData();
    } catch (err) {
      toast.error('Failed to update invoice');
    }
  };

  const resetForm = () => {
    setSelectedInvoice(null);
    setInvoiceForm({
      vendor_id: '', grn_id: '', invoice_number: '', invoice_date: '', due_date: '',
      subtotal: 0, tax_amount: 0, total_amount: 0, payment_status: 'pending', notes: '',
      items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 18, amount: 0 }]
    });
  };

  const openEditDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceForm({
      vendor_id: invoice.vendor_id || '',
      grn_id: invoice.grn_id || '',
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date?.split('T')[0] || '',
      due_date: invoice.due_date?.split('T')[0] || '',
      subtotal: invoice.subtotal || 0,
      tax_amount: invoice.tax_amount || 0,
      total_amount: invoice.total_amount || 0,
      payment_status: invoice.payment_status || 'pending',
      notes: invoice.notes || '',
      items: invoice.items?.length ? invoice.items : [{ description: '', quantity: 1, unit_price: 0, tax_rate: 18, amount: 0 }]
    });
    setInvoiceDialog(true);
  };

  const addItem = () => {
    setInvoiceForm({ ...invoiceForm, items: [...invoiceForm.items, { description: '', quantity: 1, unit_price: 0, tax_rate: 18, amount: 0 }] });
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[idx][field] = value;
    newItems[idx].amount = newItems[idx].quantity * newItems[idx].unit_price * (1 + newItems[idx].tax_rate / 100);
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  const removeItem = (idx) => {
    if (invoiceForm.items.length > 1) {
      setInvoiceForm({ ...invoiceForm, items: invoiceForm.items.filter((_, i) => i !== idx) });
    }
  };

  const getVendorName = (vendorId) => vendors.find(v => v.id === vendorId)?.name || vendorId;
  
  const calculateFormTotal = () => {
    const subtotal = invoiceForm.items.reduce((s, i) => s + (i.quantity * i.unit_price), 0);
    const tax = invoiceForm.items.reduce((s, i) => s + (i.quantity * i.unit_price * i.tax_rate / 100), 0);
    return { subtotal, tax, total: subtotal + tax };
  };

  const filteredInvoices = invoices.filter(inv => 
    inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredInvoices.length) setSelectedIds([]);
    else setSelectedIds(filteredInvoices.map(i => i.id));
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  // Summary calculations
  const pendingAmount = invoices.filter(i => i.payment_status === 'pending').reduce((s, i) => s + (i.total_amount || 0), 0);
  const overdueAmount = invoices.filter(i => i.payment_status === 'overdue').reduce((s, i) => s + (i.total_amount || 0), 0);
  const paidAmount = invoices.filter(i => i.payment_status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  const totals = calculateFormTotal();

  return (
    <div className="h-full flex flex-col bg-slate-50" data-testid="purchase-invoices-page">
      <EnterpriseToolbar
        title="Purchase Invoices"
        totalCount={filteredInvoices.length}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onRefresh={fetchData}
        onAdd={() => { resetForm(); setInvoiceDialog(true); }}
        onView={() => {
          const inv = invoices.find(i => i.id === selectedIds[0]);
          if (inv) { setSelectedInvoice(inv); setViewDialog(true); }
        }}
        onPrint={() => window.print()}
        selectedCount={selectedIds.length}
        addLabel="New Invoice"
        customActions={[
          { label: 'Mark as Paid', icon: Check, onClick: () => selectedIds.length === 1 && handleMarkPaid(selectedIds[0]), disabled: selectedIds.length !== 1 }
        ]}
      />

      {/* Summary Cards */}
      <div className="px-2 pt-1 grid grid-cols-4 gap-1.5">
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-2">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded bg-amber-100 flex items-center justify-center">
                <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
              </div>
              <div>
                <p className="text-[9px] text-amber-600 font-medium uppercase">Pending</p>
                <p className="text-sm font-bold text-amber-700 font-mono">₹{pendingAmount.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-gradient-to-br from-red-50 to-white">
          <CardContent className="p-2">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded bg-red-100 flex items-center justify-center">
                <X className="h-3.5 w-3.5 text-red-600" />
              </div>
              <div>
                <p className="text-[9px] text-red-600 font-medium uppercase">Overdue</p>
                <p className="text-sm font-bold text-red-700 font-mono">₹{overdueAmount.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-2">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[9px] text-emerald-600 font-medium uppercase">Paid</p>
                <p className="text-sm font-bold text-emerald-700 font-mono">₹{paidAmount.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-2">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded bg-blue-100 flex items-center justify-center">
                <Receipt className="h-3.5 w-3.5 text-blue-600" />
              </div>
              <div>
                <p className="text-[9px] text-blue-600 font-medium uppercase">Total</p>
                <p className="text-sm font-bold text-blue-700 font-mono">{invoices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoice List */}
      <div className="flex-1 overflow-auto">
        <Card className="mt-1 border-slate-200 shadow-sm rounded-none border-x-0">
          <CardContent className="p-0">
            <Table>
              <thead>
                <tr className="bg-slate-100 border-b">
                  <EnterpriseTableHead className="w-12">
                    <SelectCheckbox 
                      checked={selectedIds.length === filteredInvoices.length && filteredInvoices.length > 0}
                      indeterminate={selectedIds.length > 0 && selectedIds.length < filteredInvoices.length}
                      onCheckedChange={toggleSelectAll}
                    />
                  </EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable className="w-16">S.No</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Invoice Number</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Invoice Date</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Vendor</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Due Date</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable className="text-right">Amount</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Payment Status</EnterpriseTableHead>
                  <EnterpriseTableHead className="w-24">Actions</EnterpriseTableHead>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice, index) => (
                  <TableRow key={invoice.id} className={`hover:bg-slate-50 ${selectedIds.includes(invoice.id) ? 'bg-blue-50' : ''}`}>
                    <TableCell className="py-1 px-1.5">
                      <SelectCheckbox checked={selectedIds.includes(invoice.id)} onCheckedChange={() => toggleSelect(invoice.id)} />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500 py-1 px-1.5">{index + 1}</TableCell>
                    <TableCell className="font-mono text-xs font-medium text-slate-800 py-1 px-1.5">{invoice.invoice_number}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">
                      {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-slate-800 py-1 px-1.5">{invoice.vendor_name || getVendorName(invoice.vendor_id)}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">
                      {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold text-slate-800 py-1 px-1.5">
                      ₹{(invoice.total_amount || 0).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="py-1 px-1.5"><StatusBadge status={invoice.payment_status} /></TableCell>
                    <TableCell className="py-1 px-1.5">
                      <RowActions
                        onView={() => { setSelectedInvoice(invoice); setViewDialog(true); }}
                        onEdit={() => openEditDialog(invoice)}
                        customActions={invoice.payment_status === 'pending' ? [{
                          icon: Check, onClick: () => handleMarkPaid(invoice.id), title: 'Mark Paid', className: 'text-emerald-500'
                        }] : []}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
            
            {filteredInvoices.length === 0 && (
              <EmptyState
                icon={Receipt}
                title="No Purchase Invoices Found"
                description="Create your first purchase invoice to get started"
                action={
                  <Button onClick={() => { resetForm(); setInvoiceDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="h-4 w-4 mr-2" />New Invoice
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Invoice Dialog */}
      <Dialog open={invoiceDialog} onOpenChange={setInvoiceDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">{selectedInvoice ? 'Edit Invoice' : 'New Purchase Invoice'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Vendor *</Label>
                <Select value={invoiceForm.vendor_id} onValueChange={(v) => setInvoiceForm({...invoiceForm, vendor_id: v})}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Invoice Number *</Label>
                <Input value={invoiceForm.invoice_number} onChange={(e) => setInvoiceForm({...invoiceForm, invoice_number: e.target.value})} placeholder="INV-2024-001" className="bg-white font-mono" />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Link to GRN</Label>
                <Select value={invoiceForm.grn_id} onValueChange={(v) => setInvoiceForm({...invoiceForm, grn_id: v})}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {grns.map(g => <SelectItem key={g.id} value={g.id}>{g.grn_number}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Invoice Date</Label>
                <Input type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm({...invoiceForm, invoice_date: e.target.value})} />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Due Date</Label>
                <Input type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm({...invoiceForm, due_date: e.target.value})} />
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Line Items</Label>
                <Button variant="outline" size="sm" onClick={addItem}><Plus className="h-4 w-4 mr-1" />Add Line</Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <thead>
                    <tr className="bg-slate-100">
                      <EnterpriseTableHead className="w-12">#</EnterpriseTableHead>
                      <EnterpriseTableHead>Description</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-24">Qty</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-32">Unit Price</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-24">Tax %</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-32 text-right">Amount</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-12"></EnterpriseTableHead>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceForm.items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-center text-slate-500">{idx + 1}</TableCell>
                        <TableCell>
                          <Input placeholder="Item description" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} className="border-0 bg-transparent focus-visible:ring-0 px-0" />
                        </TableCell>
                        <TableCell>
                          <Input type="number" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))} className="text-center" />
                        </TableCell>
                        <TableCell>
                          <Input type="number" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', Number(e.target.value))} />
                        </TableCell>
                        <TableCell>
                          <Select value={String(item.tax_rate)} onValueChange={(v) => updateItem(idx, 'tax_rate', Number(v))}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0%</SelectItem>
                              <SelectItem value="5">5%</SelectItem>
                              <SelectItem value="12">12%</SelectItem>
                              <SelectItem value="18">18%</SelectItem>
                              <SelectItem value="28">28%</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right font-mono">₹{(item.quantity * item.unit_price * (1 + item.tax_rate / 100)).toFixed(2)}</TableCell>
                        <TableCell>
                          {invoiceForm.items.length > 1 && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => removeItem(idx)}>
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="grid gap-2">
              <Label className="text-xs font-medium text-slate-500 uppercase">Notes</Label>
              <Textarea value={invoiceForm.notes} onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})} placeholder="Additional notes..." className="min-h-[60px]" />
            </div>

            <div className="flex justify-end">
              <div className="w-72 space-y-2 p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal:</span><span className="font-mono">₹{totals.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Tax:</span><span className="font-mono">₹{totals.tax.toFixed(2)}</span></div>
                <div className="flex justify-between text-lg font-semibold border-t pt-2"><span>Total:</span><span className="font-mono text-emerald-600">₹{totals.total.toFixed(2)}</span></div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setInvoiceDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveInvoice} className="bg-emerald-600 hover:bg-emerald-700">{selectedInvoice ? 'Update' : 'Create'} Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-semibold">Invoice: {selectedInvoice?.invoice_number}</DialogTitle>
              <StatusBadge status={selectedInvoice?.payment_status} />
            </div>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg text-sm">
                <div><span className="text-slate-500">Vendor:</span><p className="font-medium">{selectedInvoice.vendor_name || getVendorName(selectedInvoice.vendor_id)}</p></div>
                <div><span className="text-slate-500">Invoice Date:</span><p className="font-medium">{selectedInvoice.invoice_date ? new Date(selectedInvoice.invoice_date).toLocaleDateString('en-IN') : '-'}</p></div>
                <div><span className="text-slate-500">Due Date:</span><p className="font-medium">{selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString('en-IN') : '-'}</p></div>
                <div><span className="text-slate-500">GRN Reference:</span><p className="font-medium font-mono">{selectedInvoice.grn_id || '-'}</p></div>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <thead><tr className="bg-slate-100">
                    <EnterpriseTableHead className="w-12">#</EnterpriseTableHead>
                    <EnterpriseTableHead>Description</EnterpriseTableHead>
                    <EnterpriseTableHead className="text-center">Qty</EnterpriseTableHead>
                    <EnterpriseTableHead className="text-right">Price</EnterpriseTableHead>
                    <EnterpriseTableHead className="text-center">Tax</EnterpriseTableHead>
                    <EnterpriseTableHead className="text-right">Amount</EnterpriseTableHead>
                  </tr></thead>
                  <tbody>
                    {selectedInvoice.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-slate-500">{idx + 1}</TableCell>
                        <TableCell className="font-medium">{item.description}</TableCell>
                        <TableCell className="text-center">{item.quantity}</TableCell>
                        <TableCell className="text-right font-mono">₹{item.unit_price}</TableCell>
                        <TableCell className="text-center">{item.tax_rate}%</TableCell>
                        <TableCell className="text-right font-mono font-medium">₹{(item.quantity * item.unit_price * (1 + item.tax_rate / 100)).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="border-t pt-4 space-y-1 text-right">
                <p className="text-sm"><span className="text-slate-500">Subtotal:</span> <span className="font-mono">₹{(selectedInvoice.subtotal || 0).toFixed(2)}</span></p>
                <p className="text-sm"><span className="text-slate-500">Tax:</span> <span className="font-mono">₹{(selectedInvoice.tax_amount || 0).toFixed(2)}</span></p>
                <p className="text-2xl font-bold text-emerald-600 font-mono">₹{(selectedInvoice.total_amount || 0).toLocaleString('en-IN')}</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button>
            {selectedInvoice?.payment_status === 'pending' && (
              <Button onClick={() => { handleMarkPaid(selectedInvoice.id); setViewDialog(false); }} className="bg-emerald-600 hover:bg-emerald-700">
                <CreditCard className="h-4 w-4 mr-2" />Mark as Paid
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
